import { computed } from 'vue';
import { useCheckoutStore } from './stores/checkout';
import { useRoute } from 'vue-router';
import { useGettext } from 'vue3-gettext';
import { usePageContext } from 'vike-vue/usePageContext';
import { Util } from '@vp/common';
import type { DateTimeFormatOptions } from 'luxon';

// Google truncates search-result titles at roughly 60 characters. The ticket-selection title is
// deliberately ordered act -> venue and location -> date, with a year-less date, so that what gets
// cut in a long title is the tail of the location rather than the act people actually searched for.
// Revisit the order together before lengthening any part.
//
// No luxon preset gives a year-less date (DATE_MED_WITH_WEEKDAY always carries the year), hence the
// literal DateTimeFormatOptions. It still goes through Util.formatTime -> toLocaleString, so it stays
// locale-aware -- this is not the hand-rolled `toFormat` pattern we avoid.
const TITLE_DATE_FORMAT: DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' };

// Only the ticket-selection page is indexed, and "Get ... Tickets" is the wrong wording for the
// payment and confirmation steps. Matched on Vike's pathname rather than `useRoute().name`:
// vue-router runs on memory history under SSR and is never navigated, so the route has no name in
// the server render -- the only render a crawler sees. `urlPathname` is the same signal
// useParentFrameSync keys off, and it carries neither the query string nor a base prefix.
// Matches /e/{id} and /e/{id}/ but not /e/{id}/checkout, /finalize or /presale/{name}.
const isTicketSelectionPath = (pathname: string): boolean => /^\/e\/[^/]+\/?$/.test(pathname);

export const useCheckoutTitle = () => {
  const checkoutStore = useCheckoutStore();
  const route = useRoute();
  const { $gettext } = useGettext();
  const pageContext = usePageContext();

  const event = computed(() => checkoutStore?.getSelectedEvent);
  const venue = computed(() => event.value?.venue);
  const eventName = computed(() => event.value?.name);
  const venueName = computed(() => venue.value?.name);

  // `support` carries the support acts separately, so the first entry of `artists` is the
  // headliner. Events with no artist records fall back to the event name.
  const headlinerName = computed(() => event.value?.artists?.[0]?.name || eventName.value);

  // `state` is absent for most non-US venues, so empty parts have to drop out rather than leave a
  // trailing comma behind.
  const venueWithLocation = computed(() =>
    [venueName.value, venue.value?.city, venue.value?.state].filter(part => part).join(', '),
  );

  const eventDate = computed(() => {
    // Both guards matter. formatTime throws on a non-empty unparseable string, and it resolves a
    // missing zone to the *process* zone -- UTC in production -- which for a late show is the wrong
    // calendar day. A dateless title beats a wrong date in a search result.
    if (!event.value?.startTime || !venue.value?.timeZone) {
      return undefined;
    }

    return Util.formatTime(event.value.startTime, {
      timezone: venue.value.timeZone,
      formatOptions: TITLE_DATE_FORMAT,
    });
  });

  const pageName = computed(() => {
    let name;
    switch (route.name) {
      case 'OrderNew':
        name = $gettext('Tickets');
        break;

      case 'OrderPayment':
        name = $gettext('Checkout');
        break;

      case 'OrderFinalize':
        name = $gettext('Processing Payment');
        break;

      case 'OrderConfirmation':
        name = $gettext('Confirmation');
        break;
    }
    return name;
  });

  const ticketSelectionTitle = computed(() =>
    [
      // Third argument disables vue3-gettext's HTML-escaping of the interpolated name. A title is
      // plain text on both routes out of here -- Vike escapes it once on its way into <title>, and
      // `useTitle` assigns document.title directly -- so escaping here too would surface an act
      // like "Hall & Oates" as a literal "Hall &amp; Oates" in the tab and the search result.
      $gettext('Get %{name} Tickets', { name: headlinerName.value as string }, true),
      venueWithLocation.value,
      eventDate.value,
    ].filter(part => part).join(' • '),
  );

  const title = computed(() => {
    // Guarding on the resolved act name keeps a not-yet-loaded event on the old format instead of
    // emitting an empty <title>.
    if (isTicketSelectionPath(pageContext.urlPathname) && headlinerName.value) {
      return ticketSelectionTitle.value;
    }

    return [eventName.value, venueName.value, pageName.value, pageContext.brandName || 'VenuePilot'].filter(v => v).join(' • ');
  });

  return {
    title,
  };
};
